// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import { endpoints } from '../../api/endpoints';
import { RootState } from '../index';

type RankGroup = {
  id: number;
  cms_data: any;
  name: void | null | string;
  current_rank: {
    rank_index: number;
    total_ranks: number;
    id: number;
    name: string;
    points: number;
    min_points_to_hold: number;
    min_points_to_upgrade: number;
    valid_until: number;
    cms_data: any;
  };
  next_rank: {
    id: number;
    name: string;
    min_points_to_hold: number;
    min_points_to_upgrade: number;
    cms_data: any;
  };
};

type Wallet = {
  id: string;
  amount: number;
  cms_data: any; //{\"name\":\"Loyalty Points\"}"
};

type FetchConfigResult = {
  rank_groups: RankGroup[];
  wallets: Wallet[];
  error?: string;
};

type FetchRanksConfigResult = any;

type FetchConfigError = {
  rejectValue: {
    error: string;
  };
};

type UpdateRankGroupAction = {
  rank_group_id: number;
  data: any;
};

type UpdateLoyaltyWalletsAction = {
  data: any;
};

export const fetchRankSystems = createAsyncThunk<FetchRanksConfigResult, void, FetchConfigError>(
  'rankSystems/list',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      const response = await axios.get(endpoints.rankSystems.list, {
        headers: {
          Authorization: 'Bearer ' + state.authentication.access_token,
        },
      });

      if (response.data) {
        return response.data;
      }

      return rejectWithValue({
        error: "Couldn't fetch rank systems list",
      });
    } catch (err: any) {
      const errResp = { error: err.toString() };
      return rejectWithValue(errResp);
    }
  },
);

export const fetchRankDetails = createAsyncThunk<FetchConfigResult, void, FetchConfigError>(
  'rankSystems/details',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      const response = await axios.get(endpoints.rankSystems.details, {
        headers: {
          Authorization: 'Bearer ' + state.authentication.access_token,
        },
      });

      if (response.data) {
        return response.data;
      }

      return rejectWithValue({
        error: "Couldn't fetch rank details",
      });
    } catch (err: any) {
      const errResp = { error: err.toString() };
      return rejectWithValue(errResp);
    }
  },
);

export const fetchLoyaltyWallets = createAsyncThunk<FetchRanksConfigResult, void, FetchConfigError>(
  'rankSystems/loyaltyWallets',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      const response = await axios.get(endpoints.rankSystems.loyaltyWallets, {
        headers: {
          Authorization: 'Bearer ' + state.authentication.access_token,
        },
      });

      if (response.data) {
        return response.data;
      }

      return rejectWithValue({
        error: "Couldn't fetch loyalty wallets list",
      });
    } catch (err: any) {
      const errResp = { error: err.toString() };
      return rejectWithValue(errResp);
    }
  },
);

export const claimRankTitlePrize = createAsyncThunk<FetchRanksConfigResult, { prizeKey: string }, FetchConfigError>(
  'rankSystems/claimRankTitlePrize',
  async ({ prizeKey }, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      const response = await axios.get(endpoints.rankSystems.claimPrize, {
        params: { key: prizeKey },
        headers: {
          Authorization: 'Bearer ' + state.authentication.access_token,
        },
      });

      if (response.data) {
        return { success: true, prizeKey, data: response.data };
      }

      return rejectWithValue({
        error: `Couldn't claim prize with key ${prizeKey}`,
      });
    } catch (err: any) {
      const errResp = { error: err.toString() };
      return rejectWithValue(errResp);
    }
  },
);

const toJson = (data: any) => {
  try {
    return JSON.parse(data);
  } catch (e) {
    return {};
  }
};

const convertArb = (arb: any) => {
  const arbData: any = {};

  arb?.images?.forEach?.(({ name, value }: any) => {
    if (name && value) {
      arbData[name] = value;
    }
  });

  Object.keys(arb?.texts ?? {})?.forEach?.((key: any) => {
    if (key && arb?.texts[key]) {
      arbData[key] = arb.texts[key];
    }
  });

  return arbData;
};

export interface RankSystemsState {
  rankGroups: RankGroup[];
  wallets: Wallet[];
  loading: boolean;
  loaded: boolean;
  error: any;

  rnkLoading: boolean;
  rnkLoaded: boolean;
  rnkError: any;
  ranks: any[];

  lwLoading: boolean;
  lwLoaded: boolean;
  lwError: any;
  loyaltyWallets: {
    currencies: any[];
    currency_groups: any[];
  };

  cpLoading: boolean;
  cpLoaded: boolean;
  cpError: any;
  claimPrize: any;
}

export const rankSystemsSlice = createSlice({
  name: 'rankSystems',
  initialState: <RankSystemsState>{
    rankGroups: [],
    wallets: [],
    loading: false,
    loaded: true,
    error: null,

    rnkLoading: false,
    rnkLoaded: true,
    rnkError: null,
    ranks: [],

    lwLoading: false,
    lwLoaded: true,
    lwError: null,
    loyaltyWallets: {
      currencies: [],
      currency_groups: [],
    },

    cpLoading: false,
    cpLoaded: false,
    cpError: null,
    claimPrize: null,
  },
  reducers: {
    updateRankGroup: (state, action) => {
      const rankGroupId = (action.payload as UpdateRankGroupAction).rank_group_id;

      const index = state.rankGroups.findIndex((el: RankGroup) => {
        return el.id === rankGroupId;
      });

      if (index > -1) {
        state.rankGroups[index] = (action.payload as UpdateRankGroupAction).data;
      }
    },
    updateCurrentRank: (state, action) => {
      const rankGroupId = (action.payload as UpdateRankGroupAction).rank_group_id;

      const index = state.rankGroups.findIndex((el: RankGroup) => {
        return el.id === rankGroupId;
      });

      if (index > -1) {
        const data = (action.payload as UpdateRankGroupAction).data;

        state.rankGroups[index].current_rank = {
          ...state.rankGroups[index].current_rank,
          ...data,
        };
      }
    },
    updateLoyaltyWallets: (state, action) => {
      state.wallets = (action.payload as UpdateLoyaltyWalletsAction).data;
    },
    clearLoyaltyState: (state, action) => {
      state.rankGroups = [];
      state.wallets = [];
      state.loading = false;
      state.loaded = true;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRankDetails.fulfilled, (state, action) => {
        state.loading = false;

        /*
        const rankGroups = action.payload.rank_groups;
        const wallets = action.payload.wallets;
        
        rankGroups?.forEach?.((rankGroup: RankGroup) => {
          rankGroup.cms_data = toJson(rankGroup.cms_data);
          rankGroup.current_rank.cms_data = toJson(rankGroup.current_rank.cms_data);
          rankGroup.next_rank.cms_data = toJson(rankGroup.next_rank.cms_data);

          if (rankGroup.current_rank?.cms_data?.ui_front?.art_bundles) {
            if (Array.isArray(rankGroup.current_rank.cms_data.ui_front.art_bundles)) {
              const arb = rankGroup.current_rank.cms_data.ui_front.art_bundles[0];
              rankGroup.current_rank.cms_data.ui_front.art_bundles = convertArb(arb);
            }
          }
          if (rankGroup.next_rank?.cms_data?.ui_front?.art_bundles) {
            if (Array.isArray(rankGroup.next_rank.cms_data.ui_front.art_bundles)) {
              const arb = rankGroup.next_rank.cms_data.ui_front.art_bundles[0];
              rankGroup.next_rank.cms_data.ui_front.art_bundles = convertArb(arb);
            }
          }
        });
        

        wallets?.forEach?.((wallet: Wallet) => {
          wallet.cms_data = toJson(wallet.cms_data);
        });
        */

        state.rankGroups = action.payload.rank_groups;
        state.wallets = action.payload.wallets;

        console.log('fetchRankDetails.fulfilled', action.payload);
      })
      .addCase(fetchRankDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRankDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
        console.log('fetchLoyaltyWallets.rejected', action.payload);
      })

      .addCase(fetchRankSystems.fulfilled, (state, action) => {
        state.rnkLoading = false;
        state.rnkLoaded = true;

        /*
        const ranks = action.payload;
        ranks?.forEach?.((rank: any) => {
          rank.cms_data = toJson(rank.cms_data);

          rank?.rankTitles?.forEach?.((rankTitle: any) => {
            rankTitle.cms_data = toJson(rankTitle.cms_data);

            if (rankTitle?.cms_data?.ui_front?.art_bundles) {
              if (Array.isArray(rankTitle.cms_data.ui_front.art_bundles)) {
                const arb = rankTitle.cms_data.ui_front.art_bundles[0];
                rankTitle.cms_data.ui_front.art_bundles = convertArb(arb);
              } else {
                rankTitle.cms_data.ui_front.art_bundles = {};
              }
            }
          });
        });
        */

        // @ts-ignore
        state.ranks = action.payload;

        console.log('fetchRankSystems.fulfilled', action.payload);
      })
      .addCase(fetchRankSystems.pending, (state) => {
        state.rnkLoading = true;
      })
      .addCase(fetchRankSystems.rejected, (state, action) => {
        state.rnkLoading = false;
        state.error = action.payload?.error;
        console.log('fetchRankSystems.rejected', action.payload);
      })

      .addCase(fetchLoyaltyWallets.fulfilled, (state, action) => {
        state.lwLoading = false;
        state.lwLoaded = true;

        state.loyaltyWallets = action.payload;

        console.log('fetchLoyaltyWallets.fulfilled', action.payload);
      })
      .addCase(fetchLoyaltyWallets.pending, (state) => {
        state.lwLoading = true;
      })
      .addCase(fetchLoyaltyWallets.rejected, (state, action) => {
        state.lwLoading = false;
        state.error = action.payload?.error;
        console.log('fetchLoyaltyWallets.rejected', action.payload);
      })

      .addCase(claimRankTitlePrize.fulfilled, (state, action) => {
        state.cpLoading = false;
        state.cpLoaded = true;

        state.claimPrize = action.payload;

        if (action.payload?.success) {
          // Update the ranks state with the claimed prize
          const prizeKey = action.payload.prizeKey;

          let found = false;
          state?.ranks?.forEach?.((rank) => {
            if (found) return; // Exit early if rankTitle is found
            rank.rank_titles?.forEach?.((title: any) => {
              if (found) return; // Exit early if rankTitle is found

              if (title?.cms_data?.ui_front?.rank_prize?.prize_key === prizeKey) {
                found = true;

                title.cms_data.ui_front.rank_prize.status = 'collected';
              }
            });
          });
        }

        console.log('claimRankTitlePrize.fulfilled', action.payload);
      })
      .addCase(claimRankTitlePrize.pending, (state) => {
        state.cpLoading = true;
      })
      .addCase(claimRankTitlePrize.rejected, (state, action) => {
        state.cpLoading = false;
        state.cpError = action.payload?.error;
        console.log('claimRankTitlePrize.rejected', action.payload);
      });
  },
});

export const { updateRankGroup, updateCurrentRank, updateLoyaltyWallets, clearLoyaltyState } = rankSystemsSlice.actions;

export default rankSystemsSlice.reducer;
